import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { tagsOf } from '../../lib/memoryTags';
import { ISearch, ITrash, ICheck, ISlash } from './DashIcons';

const DRAWER_CLOSE_MS = 250;

const timeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Rough char-per-token heuristic — used everywhere in the dashboard so
// users see a consistent estimate without round-tripping a tokenizer.
// The real count is on the backend; the inject path uses tiktoken.
const tokenEstimate = (s) => (s ? Math.ceil(s.length / 4) : 0);

const MemoriesPage = () => {
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [semantic, setSemantic] = useState(false);
  const [open, setOpen] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userKey = user?.userId || user?.id || 'anon';

  const { data: memoriesData, isLoading, error } = useQuery({
    queryKey: ['memories', userKey],
    // 10k matches the home page so users see a consistent corpus size.
    // The API returns `count = len(results)` (not the DB total), so this
    // limit is effectively the visible ceiling until /memory/count ships.
    queryFn: () => apiService.getRecentMemories({ limit: 10000 }),
    refetchInterval: 60000,
    enabled: !!user,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiService.deleteMemory(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['memories', userKey]);
      toast.success('Memory deleted');
      closeDrawer();
    },
    onError: () => toast.error('Failed to delete memory'),
  });

  const allMemories = useMemo(() => {
    const raw =
      memoriesData?.results ||
      memoriesData?.data?.blocks ||
      memoriesData?.blocks ||
      memoriesData?.data ||
      [];
    return Array.isArray(raw) ? raw : [];
  }, [memoriesData]);

  // Use the shared tagsOf helper (frontend/src/lib/memoryTags.js) so this
  // page, the home page, and Usage all agree on what counts as a tag.
  const getTags = tagsOf;

  // Compute the top tags from the loaded window for the filter pills.
  // Falls back to an empty list when no memories have loaded yet.
  const hotTags = useMemo(() => {
    const counts = {};
    allMemories.forEach((m) => {
      getTags(m).forEach((t) => {
        counts[t] = (counts[t] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([t]) => t);
  }, [allMemories]);

  // Semantic search — only fires when toggled on AND there's a query.
  // Returns scored matches from the tag-graph + propagation pipeline,
  // not vector similarity. Results are merged with the loaded list so
  // the drawer's existing memory-by-id lookup still works.
  const { data: semanticData, isFetching: semanticLoading } = useQuery({
    queryKey: ['semantic-search', userKey, search],
    queryFn: () => apiService.semanticSearch({ query: search.trim(), limit: 50 }),
    enabled: semantic && search.trim().length >= 2,
    staleTime: 30000,
  });

  const filtered = useMemo(() => {
    if (semantic && search.trim()) {
      const sem = (semanticData?.results || semanticData?.items || []) || [];
      // Map semantic results onto the same shape as recent memories so the
      // table renders without special-casing — pull metadata from the
      // loaded set when the semantic API returns id-only summaries.
      const byId = new Map(allMemories.map((m) => [m.id || m._id, m]));
      return sem.map((s) => {
        const id = s.id || s._id;
        const local = byId.get(id);
        return local ? { ...local, ...s } : s;
      });
    }
    let rows = allMemories;
    if (activeTag !== 'all') {
      rows = rows.filter((m) => getTags(m).includes(activeTag));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((m) => {
        const content = (m.content || '').toLowerCase();
        const tags = getTags(m).join(' ').toLowerCase();
        return content.includes(q) || tags.includes(q);
      });
    }
    return rows;
  }, [allMemories, activeTag, search, semantic, semanticData]);

  // Subscribe to the topbar's ⌘K search so users can filter from anywhere.
  useEffect(() => {
    const handler = (e) => setSearch(e.detail?.value || '');
    window.addEventListener('mme:topbar-query', handler);
    return () => window.removeEventListener('mme:topbar-query', handler);
  }, []);

  // Drawer visibility is driven separately from the selected memory
  // so we can play the slide-out animation before clearing state.
  const openDrawer = (memory) => {
    setOpen(memory);
    setDrawerVisible(true);
  };
  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => setOpen(null), DRAWER_CLOSE_MS);
  };

  useEffect(() => {
    if (!drawerVisible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerVisible]);

  const truncate = (s, len = 140) => {
    if (!s) return '—';
    return s.length > len ? `${s.slice(0, len)}…` : s;
  };

  const memoryId = open?.id || open?._id;

  // Real retrieval rationale: re-run an inject using this memory's own
  // content as the prompt and find this memory in the returned items.
  // The score object on each item has the real component breakdown
  // (activation, recency, importance, statusBonus, diversityPenalty,
  // total) — what the user sees here is exactly what the scorer
  // produced. If this memory ranks below the top-K, it just won't
  // appear and we show that as a (still informative) state.
  const RATIONALE_LIMIT = 10;
  const rationalePrompt = open?.content
    ? open.content.slice(0, 280)
    : '';
  const {
    data: rationaleData,
    isFetching: rationaleLoading,
    error: rationaleError,
  } = useQuery({
    queryKey: ['memory-rationale', memoryId, rationalePrompt],
    queryFn: () =>
      apiService.injectMemory({
        prompt: rationalePrompt,
        limit: RATIONALE_LIMIT,
        debug: true,
      }),
    enabled: !!open && rationalePrompt.length > 0,
    staleTime: 30000,
    retry: 1,
  });

  const matchedItem = useMemo(() => {
    if (!rationaleData || !memoryId) return null;
    const items = rationaleData?.items || [];
    return items.find((it) => it.id === memoryId || it._id === memoryId) || null;
  }, [rationaleData, memoryId]);

  const matchedRank = useMemo(() => {
    if (!rationaleData || !memoryId) return null;
    const items = rationaleData?.items || [];
    const idx = items.findIndex((it) => it.id === memoryId || it._id === memoryId);
    return idx >= 0 ? idx + 1 : null;
  }, [rationaleData, memoryId]);

  // The score object's components map 1:1 onto the rows we render.
  // Diversity is a *penalty* in the scorer (subtracted), so we visualize
  // it as a separate row labeled accordingly. Status bonus is the small
  // `{draft:0, submitted:0.5, completed:1}` term.
  const ratRows = useMemo(() => {
    const score = matchedItem?.score;
    if (!score) return [];
    return [
      { k: 'total', v: score.total, kind: 'sum' },
      { k: 'activation', v: score.activation },
      { k: 'recency', v: score.recency },
      { k: 'importance', v: Math.min(score.importance / 5, 1), raw: score.importance },
      { k: 'status bonus', v: score.statusBonus },
      { k: 'diversity penalty', v: score.diversityPenalty, kind: 'penalty' },
    ];
  }, [matchedItem]);

  // Pipeline trace from the inject debug payload.
  const seedTags = rationaleData?.seedTags || [];
  const activatedCount = rationaleData?.debug?.activatedTags?.length || 0;
  const candidatesCount = rationaleData?.debug?.candidatesCount ?? 0;
  const scoredCount = rationaleData?.debug?.scoredCount ?? 0;

  return (
    <>
      <Helmet><title>Memories · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <h1>Memories</h1>
          <div className="sub">
            Every block your agent has saved, scored, and tagged. Click any row to see the retrieval rationale.
          </div>
        </div>

        <div className="filter-row">
          <div className="search" style={{ flex: 1, maxWidth: 420 }}>
            <ISearch size={14} />
            <input
              type="text"
              placeholder="Search content or tags…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search memories"
            />
          </div>
          <button
            type="button"
            className={`filter-pill ${semantic ? 'on' : ''}`}
            onClick={() => setSemantic((v) => !v)}
            title="Use the tag-graph + propagation pipeline instead of substring match"
          >
            {semanticLoading ? '⋯ semantic' : 'semantic'}
          </button>
          <button
            type="button"
            className={`filter-pill ${activeTag === 'all' ? 'on' : ''}`}
            onClick={() => setActiveTag('all')}
            disabled={semantic}
          >
            all tags
          </button>
          {hotTags.map((t) => (
            <button
              key={t}
              type="button"
              className={`filter-pill ${activeTag === t ? 'on' : ''}`}
              onClick={() => setActiveTag(t)}
            >
              {t}
            </button>
          ))}
          <span className="filter-meta">
            {filtered.length} / {allMemories.length} memories
          </span>
        </div>

        {error && (
          <div className="error-banner">
            {error?.response?.status === 401
              ? 'Your session may have expired. Try signing out and back in.'
              : 'Could not load memories — the API may be temporarily unavailable.'}
          </div>
        )}

        <div className="panel">
          {isLoading && (
            <div className="empty">
              <span className="spinner" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Loading memories…
            </div>
          )}
          {!isLoading && allMemories.length === 0 && (
            <div className="empty">
              <div className="title">No memories yet</div>
              <div>
                Connect an MCP client or call <code style={{ fontFamily: 'var(--mono)' }}>/memory/save</code> to get started.
              </div>
            </div>
          )}
          {!isLoading && allMemories.length > 0 && filtered.length === 0 && (
            <div className="empty">No memories match your filter.</div>
          )}
          {!isLoading && filtered.length > 0 && (
            <table className="mtable">
              <thead>
                <tr>
                  <th>Memory</th>
                  <th style={{ width: 220 }}>Tags</th>
                  <th style={{ width: 110 }}>Tokens</th>
                  <th style={{ width: 100 }}>Saved</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, idx) => {
                  const id = m.id || m._id || `row-${idx}`;
                  const tags = getTags(m);
                  const tokens =
                    m.tokens || m.tokenCount || tokenEstimate(m.content || '');
                  const ts = m.created_at || m.createdAt || m.timestamp;
                  return (
                    <tr key={id} className="clickable" onClick={() => openDrawer(m)}>
                      <td style={{ maxWidth: 460 }}>{truncate(m.content)}</td>
                      <td>
                        {tags.length === 0 && <span className="tag">untagged</span>}
                        {tags.slice(0, 4).map((t) => (
                          <span key={t} className={`tag ${hotTags.includes(t) ? 'hot' : ''}`}>
                            {t}
                          </span>
                        ))}
                        {tags.length > 4 && <span className="tag">+{tags.length - 4}</span>}
                      </td>
                      <td>
                        <span className="score">
                          {tokens}
                          <em style={{ marginLeft: 2, color: 'var(--ink-4)', fontStyle: 'normal' }}>t</em>
                          <span className="bar">
                            <div style={{ width: `${Math.min(tokens / 500, 1) * 100}%` }} />
                          </span>
                        </span>
                      </td>
                      <td className="ts">{timeAgo(ts)}</td>
                      <td>
                        <button
                          type="button"
                          className="icon-btn danger"
                          aria-label="Delete memory"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this memory?')) {
                              deleteMutation.mutate(m.id || m._id);
                            }
                          }}
                        >
                          <ITrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Rationale drawer */}
      <div
        className={`drawer-backdrop ${drawerVisible ? 'on' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <aside
        className={`drawer ${drawerVisible ? 'on' : ''}`}
        role="dialog"
        aria-label="Memory retrieval rationale"
      >
        {open && (
          <>
            <div className="drawer-head">
              <div>
                <div className="drawer-eyebrow">Memory · {memoryId || 'unknown'}</div>
                <h3>Retrieval rationale</h3>
              </div>
              <button type="button" className="drawer-close" onClick={closeDrawer}>
                esc
              </button>
            </div>
            <div className="drawer-body">
              <div className="drawer-content-block">{open.content || '—'}</div>

              <div style={{ marginBottom: 18 }}>
                <div className="drawer-eyebrow-sm">Tags</div>
                <div>
                  {getTags(open).length === 0 ? (
                    <span className="tag">untagged</span>
                  ) : (
                    getTags(open).map((t) => (
                      <span key={t} className={`tag ${hotTags.includes(t) ? 'hot' : ''}`}>
                        {t}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="drawer-eyebrow-sm">
                Retrieval rationale
                {matchedRank && (
                  <span style={{ marginLeft: 8, color: 'var(--ink-4)', fontSize: 11 }}>
                    · ranked #{matchedRank} of top {RATIONALE_LIMIT} for its own content
                  </span>
                )}
              </div>
              <div style={{ padding: '8px 0' }}>
                {rationaleLoading && !matchedItem && (
                  <div className="empty" style={{ padding: '14px 0' }}>
                    <span className="spinner" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Computing real scores via /memory/inject…
                  </div>
                )}
                {!rationaleLoading && rationaleError && (
                  <div className="empty" style={{ padding: '14px 0', color: 'var(--ink-4)' }}>
                    Couldn&apos;t fetch retrieval rationale. {rationaleError?.response?.status === 401
                      ? 'Sign in expired.'
                      : 'Service may be temporarily unavailable.'}
                  </div>
                )}
                {!rationaleLoading && !rationaleError && !matchedItem && rationaleData && (
                  <div className="empty" style={{ padding: '14px 0', color: 'var(--ink-4)', lineHeight: 1.5 }}>
                    This memory ranks below the top {RATIONALE_LIMIT} for its own content. The retriever surfaces other memories first — usually because they&apos;re newer, share more activated tags, or have higher importance.
                  </div>
                )}
                {matchedItem && ratRows.map((r) => {
                  // Diversity is subtracted in the scorer; visualize it
                  // as red so the user sees it eats score, not adds it.
                  const isPenalty = r.kind === 'penalty';
                  const isSum = r.kind === 'sum';
                  const pct = Math.max(0, Math.min(1, r.v)) * 100;
                  return (
                    <div key={r.k} className="rat-row">
                      <span className="k">{r.k}</span>
                      <div className="bar">
                        <div
                          style={{
                            width: `${pct}%`,
                            background: isPenalty
                              ? 'var(--accent, #c0382c)'
                              : isSum
                              ? 'var(--ink, #0a0a0a)'
                              : undefined,
                          }}
                        />
                      </div>
                      <span className="v">
                        {r.raw !== undefined ? r.raw.toFixed(2) : r.v.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="drawer-eyebrow-sm" style={{ marginTop: 20 }}>
                Pipeline trace
              </div>
              <div className="graph-path">
                <span className="g-seed">
                  {seedTags.slice(0, 3).join(' · ') || (getTags(open)[0] || 'seed')}
                </span>
                <span className="g-sep"> → </span>
                <span className="g-neighbor">
                  {activatedCount > 0
                    ? `${activatedCount} activated tags`
                    : 'no propagation'}
                </span>
                <span className="g-sep"> → </span>
                <span className="g-target">
                  {candidatesCount > 0
                    ? `${candidatesCount} candidates`
                    : 'no matches'}
                </span>
                <div className="g-meta">
                  scored {scoredCount} · selected {rationaleData?.debug?.selectedCount ?? 0}
                  {rationaleData?.debug?.propagationEnabled === false && ' · propagation off'}
                  {rationaleData?.tokenBudget && ` · budget ${rationaleData.tokenBudget}t`}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => toast.success('Feedback recorded — useful')}
                >
                  <ICheck size={14} /> Mark useful
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => toast('Feedback recorded — unhelpful', { icon: '·' })}
                >
                  <ISlash size={14} /> Unhelpful
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-danger"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => {
                    if (window.confirm('Delete this memory?')) {
                      deleteMutation.mutate(memoryId);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <ITrash size={14} /> Delete
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default MemoriesPage;
